import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-[#003366] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-white/70 mt-2">Last Updated: December 2024</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          
          <p className="text-gray-700 leading-relaxed">
            Valenrock™ Remodelers, LLC, doing business as Valenrock™ Home Services ("Valenrock," "we," "us," or "our"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Information We Collect</h2>
          
          <h3 className="text-xl font-semibold text-[#003366] mt-6 mb-3">Personal Information</h3>
          <p className="text-gray-700">We may collect personal information that you voluntarily provide, including:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Name and contact information (email, phone number)</li>
            <li>Property address and location</li>
            <li>Information about your home and ownership history</li>
            <li>Service preferences and interests</li>
            <li>Messages and communications you send us</li>
          </ul>

          <h3 className="text-xl font-semibold text-[#003366] mt-6 mb-3">Automatically Collected Information</h3>
          <p className="text-gray-700">When you visit our website, we may automatically collect:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>IP address</li>
            <li>Pages visited and time spent on pages</li>
            <li>Referring website addresses</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">How We Use Your Information</h2>
          <p className="text-gray-700">We use the information we collect to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Provide, maintain, and improve our services</li>
            <li>Respond to your inquiries and schedule assessments</li>
            <li>Create and maintain your Home Profile</li>
            <li>Send you service-related communications</li>
            <li>Send promotional information (with your consent)</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Information Sharing</h2>
          <p className="text-gray-700 mb-4"><strong>We do not sell, rent, lease, or trade your personal information to third parties for marketing purposes. Ever.</strong></p>
          <p className="text-gray-700">We may share your information only in the following limited circumstances:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li><strong>Service Providers:</strong> Trusted contractors and vendors who assist in delivering our services, bound by confidentiality agreements</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our legal rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with prior notice to you)</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Home Profile & Assessment Data</h2>
          <p className="text-gray-700 mb-4">
            Your Home Profile and assessment reports contain sensitive information about your property. We take extra precautions to protect this data:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <p className="text-gray-700"><strong>Confidentiality:</strong> Your Home Profile™ data is treated as strictly confidential and is never shared with third parties for marketing or sales purposes</p>
            <li><strong>Access Control:</strong> Only authorized Valenrock personnel directly involved in your service have access to your assessment data</li>
            <li><strong>No Data Monetization:</strong> We do not analyze, aggregate, or sell property data from our assessments</li>
            <li><strong>Client Ownership:</strong> You retain ownership of your Home Profile™ information and may request deletion at any time</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Data Security</h2>
          <p className="text-gray-700 mb-4">
            We implement industry-standard security measures to protect your personal information:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Encrypted data transmission (SSL/TLS)</li>
            <li>Secure cloud database storage with access controls</li>
            <li>Regular security reviews and updates</li>
            <li>Limited employee access on a need-to-know basis</li>
          </ul>
          <p className="text-gray-700 mt-4">
            While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security but will notify you promptly in the unlikely event of a data breach affecting your personal information.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Data Breach Notification</h2>
          <p className="text-gray-700">
            In the event of a data breach that affects your personal information, we will:
          </p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Notify affected individuals within 72 hours of discovering the breach</li>
            <li>Provide details about what information was affected</li>
            <li>Explain steps we are taking to address the breach</li>
            <li>Offer guidance on how you can protect yourself</li>
            <li>Report to relevant authorities as required by law</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Your Rights</h2>
          <p className="text-gray-700">Depending on your location, you may have the right to:</p>
          <ul className="list-disc pl-6 text-gray-700 space-y-2">
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your information</li>
            <li>Opt out of marketing communications</li>
            <li>Withdraw consent where applicable</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Cookies</h2>
          <p className="text-gray-700">
            Our website may use cookies and similar tracking technologies to enhance your experience. You can control cookies through your browser settings.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Third-Party Links</h2>
          <p className="text-gray-700">
            Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Children's Privacy</h2>
          <p className="text-gray-700">
            Our services are not directed to individuals under 18. We do not knowingly collect personal information from children.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Changes to This Policy</h2>
          <p className="text-gray-700">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
          </p>

          <h2 className="text-2xl font-bold text-[#003366] mt-8 mb-4">Contact Us</h2>
          <p className="text-gray-700">
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <div className="bg-gray-50 p-4 rounded-lg mt-4">
            <p className="text-gray-700"><strong>Valenrock™ Remodelers, LLC d/b/a Valenrock™ Home Services</strong></p>
            <p className="text-gray-700">Email: contact@valenrockhome.services</p>
            <p className="text-gray-700">Phone: (551) 379-9774</p>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link to="/" className="text-[#003366] hover:text-[#CFB973] font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
